<template>
  <fieldset class="qr-gates" data-testid="qr-gates">
    <legend class="common-label-text">QR onboarding</legend>

    <div class="row">
      <div class="col-sm-12 o-modal-form-field">
        <label class="o-toggle-inline">
          <input type="checkbox" v-model="profile.qr_enabled" @change="onEnabledChange" data-testid="qr-enabled">
          QR code enabled
        </label>
        <p class="qr-gates__lede text-muted">
          While this is on, <strong>anyone holding the code can start an intake</strong> —
          a poster, a screenshot, a photo of a poster. The settings below are what
          bound that.
        </p>
      </div>
    </div>

    <!-- ⚠ #1890 (ed) — ALL FOUR ARE ALWAYS VISIBLE, the three limits disabled
         until the switch is on. They used to render only once QR was enabled,
         which put the door open before the cap and the expiry were set. An
         admin should choose a safe bound first and then open it. -->
    <div class="row u-margin-top-10" :class="{ 'qr-gates--off': !profile.qr_enabled }">
      <div class="col-sm-4 o-modal-form-field">
        <!-- #1908 — it genuinely IS the last valid day now: the QR stays open for
             the whole of it, in the local's own timezone, and refuses from their
             next midnight. The label used to imply a cut-off at the START of the
             day, which is what it actually did. -->
        <label class="common-label-text">Last day the QR works</label>
        <input type="date" class="form-control" v-model="profile.qr_active_until"
               :disabled="!profile.qr_enabled" data-testid="qr-active-until">
        <small class="text-muted">Open for the whole of this day, your local time.</small>
      </div>
      <div class="col-sm-4 o-modal-form-field">
        <label class="common-label-text">Daily cap (0–1000)</label>
        <input type="number" min="0" max="1000" class="form-control"
               v-model.number="profile.qr_daily_cap"
               :disabled="!profile.qr_enabled" data-testid="qr-daily-cap">
      </div>
      <div class="col-sm-4 o-modal-form-field">
        <label class="o-toggle-inline">
          <input type="checkbox" v-model="profile.qr_require_sms_verify"
                 :disabled="!profile.qr_enabled" data-testid="qr-require-sms">
          Require SMS verification
        </label>
      </div>
    </div>

    <div class="row" v-if="showUsage">
      <div class="col-sm-12">
        <p class="qr-gates__usage text-muted" data-testid="qr-usage">{{ usageLine }}</p>
      </div>
    </div>

    <!-- Shown as it is typed, and again in the confirm on save. Seeing it once
         while editing is what stops the confirm being the first time anyone
         reads it. -->
    <div class="row" v-if="warningList.length">
      <div class="col-sm-12">
        <ul class="qr-gates__warnings" data-testid="qr-warnings">
          <li v-for="w in warningList" :key="w.key" :data-testid="'qr-warn-' + w.key">{{ w.text }}</li>
        </ul>
      </div>
    </div>
  </fieldset>
</template>

<script>
/**
 * #1890 — the four QR gates (#1827), in ONE place.
 *
 * They were enforced on every public request from the day #1827 shipped and
 * changeable only by a hand-written UPDATE on production. !907 gave them a UI,
 * but on `onboarding/OnboardingProfiles.vue` — a screen reachable only by its
 * own route, which nothing in the menu links to. The screen ed opens is
 * Config → Onboarding.
 *
 * ⚠ ONE COMPONENT, TWO HOSTS, deliberately. Two copies of a consent-and-limits
 * control will drift, and the copy that drifts is the one nobody is looking at
 * — which is the whole shape of this issue. The rules themselves already live
 * in `qrGateRules.js` and are unit-tested there; this file is the markup.
 *
 * The prop is MUTATED rather than round-tripped through `input`. Both hosts
 * keep the profile under edit as one plain object that they replace wholesale
 * on open and read wholesale on save, so writing into it is what keeps this a
 * drop-in — and it is the same object, not a replaced prop.
 */
import { qrWarnings, usageText, defaultActiveUntil } from '../../qrGateRules.js';

export default {
  name: 'QrGates',
  props: {
    /** The profile being edited. Carries qr_enabled / qr_active_until / qr_daily_cap / qr_require_sms_verify. */
    profile: { type: Object, required: true },
    /** Today's issuance for this profile; null on an unsaved one. */
    issuedToday: { type: Number, default: null },
    /** Usage has no meaning before the profile exists. */
    showUsage: { type: Boolean, default: false },
  },
  computed: {
    warningList() { return qrWarnings(this.profile); },
    usageLine() { return usageText(this.issuedToday, this.profile.qr_daily_cap); },
  },
  methods: {
    /**
     * Switching the QR on suggests an expiry rather than leaving it blank. A QR
     * printed on a poster outlives the event it was made for, and "no expiry"
     * is what a blank field quietly means.
     */
    onEnabledChange() {
      if (this.profile.qr_enabled && !this.profile.qr_active_until) {
        this.$set(this.profile, 'qr_active_until', defaultActiveUntil());
      }
      this.$emit('change', this.profile);
    },
  },
};
</script>

<style scoped>
.qr-gates { border: 1px solid #e3e6ec; border-radius: 3px; padding: 12px 16px 4px; margin: 0; }
.qr-gates legend { width: auto; border: 0; margin-bottom: 4px; font-size: 14px; }
.qr-gates__lede { margin: 6px 0 0; font-size: 12px; }
.qr-gates--off { opacity: 0.55; }
.qr-gates__usage { margin: 8px 0 0; font-size: 12px; }
.qr-gates__warnings { margin: 8px 0 0; padding-left: 18px; color: #a15c00; font-size: 12px; }
</style>
